import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Grid,
    Card,
    Title,
    Select,
    Text,
    Badge,
    Group,
    Stack,
    Paper,
    ScrollArea,
    LoadingOverlay,
    Alert,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';

// Component definitions
const CustomerHeader = ({ selectedCustomer, onCustomerChange, customers, isLoading }) => (
    <Paper mb="lg" p="md">
        <Grid>
            <Grid.Col span={6}>
                <Text
                    component="h1"
                    style={{
                        fontFamily: 'Orbitron, sans-serif',
                        color: '#FDB813',
                        fontStyle: 'italic',
                        fontSize: '1.5rem',
                        marginBottom: 0,
                        textAlign: 'left'
                    }}
                >
                    Customer Inventory Matcher
                </Text>
            </Grid.Col>
            <Grid.Col span={6}>
                <Select
                    label="Customer Selection"
                    placeholder="Select a customer"
                    data={customers}
                    value={selectedCustomer}
                    onChange={onCustomerChange}
                    searchable
                    disabled={isLoading}
                />
            </Grid.Col>
        </Grid>
    </Paper>
);

const PentagonFleetInfo = ({ customer }) => {
    const { data: fleetData, isLoading, error } = useAuthenticatedData(
        customer && customer !== '' ? `${config.apiBaseUrl}/InventoryMatcher/${customer}/pentagon-fleet` : null
    );

    return (
        <Card shadow="sm" radius="md" withBorder h="100%" pos="relative">
            <LoadingOverlay visible={isLoading} />
            <Title order={4} mb="md">Pentagon Fleet Information</Title>
            {!customer ? (
                <Text c="dimmed">Please select a customer to view fleet data</Text>
            ) : error ? (
                <Alert color="red">Error loading fleet data</Alert>
            ) : !fleetData || !Array.isArray(fleetData) || fleetData.length === 0 ? (
                <Text c="dimmed">No fleet data available</Text>
            ) : (
                <Stack gap="xs">
                    {fleetData.map((item, index) => {
                        const values = item?.values || item || {};
                        return (
                            <Group key={index} justify="space-between">
                                <Text size="sm">{values.platform || values.fleet || 'Unknown'}</Text>
                            </Group>
                        );
                    })}
                </Stack>
            )}
        </Card>
    );
};

const CiriumFleetInfo = ({ customer }) => {
    const { data: fleetData, isLoading, error } = useAuthenticatedData(
        customer && customer !== '' ? `${config.apiBaseUrl}/InventoryMatcher/${customer}/cirium-fleet` : null
    );

    return (
        <Card shadow="sm" radius="md" withBorder h="100%" pos="relative">
            <LoadingOverlay visible={isLoading} />
            <Title order={4} mb="md">Cirium Fleet Information</Title>
            {!customer ? (
                <Text c="dimmed">Please select a customer to view fleet data</Text>
            ) : error ? (
                <Alert color="red">Error loading fleet data</Alert>
            ) : !fleetData || !Array.isArray(fleetData) || fleetData.length === 0 ? (
                <Text c="dimmed">No fleet data available</Text>
            ) : (
                <ScrollArea h={300}>
                    <Stack gap="xs">
                        {fleetData.map((item, index) => {
                            const values = item?.values || item || {};
                            return (
                                <Paper key={index} p="xs" withBorder>
                                    <Group justify="space-between">
                                        <Text size="sm" fw={500}>
                                            {values.type || values.aircraft || values.platform || 'Unknown'}
                                        </Text>
                                        <Group gap="xs">
                                            <Badge variant="light">
                                                {values.count || values.quantity || '0'}
                                            </Badge>
                                            {values.avgage && (
                                                <Badge variant="outline">
                                                    {Math.round(values.avgage * 10) / 10} yrs
                                                </Badge>
                                            )}
                                        </Group>
                                    </Group>
                                </Paper>
                            );
                        })}
                    </Stack>
                </ScrollArea>
            )}
        </Card>
    );
};

const RelatedAccounts = ({ customer }) => {
    const { data: accounts, isLoading, error } = useAuthenticatedData(
        customer && customer !== '' ? `${config.apiBaseUrl}/InventoryMatcher/${customer}/related-accounts` : null
    );

    // Check if error is 404 (no accounts found)
    const isNoAccountsFound = error && (error.status === 404 || error.message?.includes('404'));

    return (
        <Card shadow="sm" radius="md" withBorder h="100%" pos="relative">
            <LoadingOverlay visible={isLoading} />
            <Title order={4} mb="md">Related Accounts</Title>
            {!customer ? (
                <Text c="dimmed">Please select a customer to view related accounts</Text>
            ) : isNoAccountsFound ? (
                <Text c="dimmed">No related accounts found</Text>
            ) : error ? (
                <Alert color="red">Error loading related accounts</Alert>
            ) : !accounts || !Array.isArray(accounts) || accounts.length === 0 ? (
                <Text c="dimmed">No related accounts</Text>
            ) : (
                <Stack gap="xs">
                    {accounts.map((account, index) => {
                        const values = account?.values || account || {};
                        const accountNumber = values.accountNumber || values.acctno || values || 'Unknown';
                        return (
                            <Badge key={index} variant="outline" size="lg">
                                {accountNumber}
                            </Badge>
                        );
                    })}
                </Stack>
            )}
        </Card>
    );
};

const RelatedInventoryList = ({ customer }) => {
    const { data: inventoryData, isLoading, error } = useAuthenticatedData(
        customer && customer !== '' ? `${config.apiBaseUrl}/InventoryMatcher/${customer}/inventory` : null
    );

    // Filter states
    const [classFilter, setClassFilter] = useState('');
    const [platformFilter, setPlatformFilter] = useState('');
    const [conditionFilter, setConditionFilter] = useState('');

    const getConditionColor = (condition) => {
        switch (condition?.toUpperCase()) {
            case 'NE': return 'green';
            case 'OH': return 'blue';
            case 'SV': return 'yellow';
            case 'AR': return 'orange';
            case 'NS': return 'purple';
            default: return 'gray';
        }
    };

    // Transform inventory data to handle the values structure
    const transformedData = useMemo(() => {
        if (!inventoryData || !Array.isArray(inventoryData)) return [];

        return inventoryData.map(item => {
            const values = item.values || item;
            return values;
        }).filter(Boolean); // Filter out any null/undefined items
    }, [inventoryData]);

    // Get unique values for filter dropdowns with better error handling
    const filterOptions = useMemo(() => {
        if (!transformedData.length) return { classes: [], platforms: [], conditions: [] };

        try {
            // Use consistent property names and add safety checks
            const classes = [...new Set(
                transformedData
                    .map(item => item?.class)
                    .filter(Boolean)
            )].sort();

            const platforms = [...new Set(
                transformedData
                    .map(item => item?.platform || item?.platforms) // Handle both singular and plural
                    .filter(Boolean)
            )].sort();

            const conditions = [...new Set(
                transformedData
                    .map(item => item?.condition)
                    .filter(Boolean)
            )].sort();

            return {
                classes: classes.map(c => ({ value: c, label: c })),
                platforms: platforms.map(p => ({ value: p, label: p })),
                conditions: conditions.map(c => ({ value: c, label: c }))
            };
        } catch (err) {
            console.warn('Error processing filter options:', err);
            return { classes: [], platforms: [], conditions: [] };
        }
    }, [transformedData]);

    // Apply filters to data with safety checks
    const filteredData = useMemo(() => {
        if (!transformedData.length) return [];

        let filtered = transformedData;

        if (classFilter) {
            filtered = filtered.filter(item => item?.class === classFilter);
        }

        if (platformFilter) {
            // Handle both platform and platforms properties
            filtered = filtered.filter(item =>
                item?.platform === platformFilter || item?.platforms === platformFilter
            );
        }

        if (conditionFilter) {
            filtered = filtered.filter(item => item?.condition === conditionFilter);
        }

        return filtered;
    }, [transformedData, classFilter, platformFilter, conditionFilter]);

    const columns = useMemo(() => [
        {
            accessorKey: 'partnumber',
            header: 'Part Number',
            size: 150,
            Cell: ({ cell }) => (
                <Text size="sm" fw={500}>{cell.getValue() || 'N/A'}</Text>
            ),
        },
        {
            accessorKey: 'description',
            header: 'Description',
            size: 250,
            Cell: ({ cell }) => (
                <Text size="sm">{cell.getValue() || 'N/A'}</Text>
            ),
        },
        {
            accessorKey: 'class',
            header: 'Class',
            size: 100,
            Cell: ({ cell }) => (
                <Text size="sm">{cell.getValue() || 'N/A'}</Text>
            ),
        },
        {
            accessorKey: 'platform',
            header: 'Platform',
            size: 150,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? (
                    <Badge variant="light" size="sm">{value}</Badge>
                ) : (
                    <Text size="sm" c="dimmed">N/A</Text>
                );
            },
        },
        {
            accessorKey: 'qty',
            header: 'Qty',
            size: 80,
            Cell: ({ cell }) => (
                <Text size="sm" fw={500}>{cell.getValue() || '0'}</Text>
            ),
        },
        {
            accessorKey: 'condition',
            header: 'Condition',
            size: 100,
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? (
                    <Badge color={getConditionColor(value)} size="sm">
                        {value}
                    </Badge>
                ) : (
                    <Text size="sm" c="dimmed">N/A</Text>
                );
            },
        },
        {
            accessorKey: 'location',
            header: 'Location',
            size: 120,
            Cell: ({ cell }) => (
                <Text size="sm">{cell.getValue() || 'N/A'}</Text>
            ),
        },
        {
            accessorKey: 'virtualwh',
            header: 'Virtual WH',
            size: 100,
            Cell: ({ cell }) => (
                <Text size="sm">{cell.getValue() || 'N/A'}</Text>
            ),
        },
        {
            accessorKey: 'dos',
            header: 'Days on Shelf',
            size: 120,
            Cell: ({ cell }) => (
                <Text size="sm">
                    {cell.getValue() ? `${cell.getValue()} days` : 'N/A'}
                </Text>
            ),
        },
    ], []);

    const table = useMantineReactTable({
        columns,
        data: filteredData,
        enableColumnFilterModes: true,
        enableColumnOrdering: true,
        enableFacetedValues: true,
        enableGrouping: true,
        enablePinning: true,
        enableRowActions: false,
        enableColumnResizing: true,
        initialState: {
            density: 'xs',
            showColumnFilters: false,
            showGlobalFilter: true,
            pagination: {
                pageIndex: 0,
                pageSize: 25,
            },
        },
        mantineSearchTextInputProps: {
            placeholder: 'Search inventory...',
        },
        mantinePaginationProps: {
            radius: 'xl',
            size: 'lg',
        },
        mantineToolbarAlertBannerProps: isLoading
            ? {
                color: 'blue',
                children: 'Loading inventory data...',
            }
            : undefined,
        state: {
            isLoading,
            showAlertBanner: isLoading,
            showProgressBars: isLoading,
        },
    });

    // Clear all filters function
    const clearFilters = () => {
        setClassFilter('');
        setPlatformFilter('');
        setConditionFilter('');
    };

    return (
        <Card shadow="sm" radius="md" withBorder>
            <Title order={4} mb="md">Related Inventory List</Title>
            {!customer ? (
                <Text c="dimmed" ta="center" py="xl">
                    Please select a customer to view matching inventory
                </Text>
            ) : error ? (
                <Alert color="red">Error loading inventory: {error.message}</Alert>
            ) : (
                <>
                    {/* Filter Controls */}
                    <Paper p="md" mb="md" withBorder>
                        <Group mb="xs">
                            <Text size="sm" fw={500}>Quick Filters:</Text>
                            <Badge
                                variant="light"
                                style={{ cursor: 'pointer' }}
                                onClick={clearFilters}
                            >
                                Clear All
                            </Badge>
                        </Group>
                        <Grid>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <Select
                                    label="Class"
                                    placeholder="All Classes"
                                    data={filterOptions.classes}
                                    value={classFilter}
                                    onChange={setClassFilter}
                                    clearable
                                    size="sm"
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <Select
                                    label="Platform"
                                    placeholder="All Platforms"
                                    data={filterOptions.platforms}
                                    value={platformFilter}
                                    onChange={setPlatformFilter}
                                    clearable
                                    size="sm"
                                />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, sm: 4 }}>
                                <Select
                                    label="Condition"
                                    placeholder="All Conditions"
                                    data={filterOptions.conditions}
                                    value={conditionFilter}
                                    onChange={setConditionFilter}
                                    clearable
                                    size="sm"
                                />
                            </Grid.Col>
                        </Grid>
                        {(classFilter || platformFilter || conditionFilter) && (
                            <Group mt="xs">
                                <Text size="xs" c="dimmed">
                                    Showing {filteredData.length} of {transformedData.length} items
                                </Text>
                            </Group>
                        )}
                    </Paper>

                    <MantineReactTable table={table} />
                </>
            )}
        </Card>
    );
};

// Main component
const CustomerFleetDashboard = () => {
    const [selectedCustomer, setSelectedCustomer] = useState('');

    // Fetch customers list
    const { data: customersData, isLoading: customersLoading, error: customersError } =
        useAuthenticatedData(`${config.apiBaseUrl}/InventoryMatcher/customers`);

    // Transform customers data for Select component
    const customers = useMemo(() => {
        if (!customersData || !Array.isArray(customersData)) return [];

        return customersData.map((customer, index) => {
            // Handle GenericScorecardValueRow structure with nested values
            const values = customer?.values || customer || {};
            const accountNumber = values.acctno || `customer-${index}`;
            const customerName = values.name || accountNumber;

            return {
                value: accountNumber,
                label: customerName
            };
        });
    }, [customersData]);

    // Don't auto-select first customer - let user choose
    // Remove the auto-selection useEffect

    if (customersError) {
        return (
            <Container size="xl" py="md">
                <Alert color="red" title="Error">
                    Failed to load customers: {customersError.message}
                </Alert>
            </Container>
        );
    }

    return (
        <Container size="xl" py="md">
            <CustomerHeader
                selectedCustomer={selectedCustomer}
                onCustomerChange={setSelectedCustomer}
                customers={customers}
                isLoading={customersLoading}
            />

            <Grid mb="lg">
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <PentagonFleetInfo customer={selectedCustomer} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <CiriumFleetInfo customer={selectedCustomer} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <RelatedAccounts customer={selectedCustomer} />
                </Grid.Col>
            </Grid>

            <RelatedInventoryList customer={selectedCustomer} />
        </Container>
    );
};

export default CustomerFleetDashboard;